import React, { useEffect, useMemo, useState } from "react";
import { CatalogService } from "../../../services/catalog.service";
import { appConfirm } from "../../../utils/appPopup";
import NestedCategoryManager from "./NestedCategoryManager";

function normalizeList(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.items)) return json.items;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.rows)) return json.rows;
  if (Array.isArray(json?.result)) return json.result;
  return [];
}

function toSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function getImageUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;

  const base = String(
    process.env.REACT_APP_CATALOG_BASE_URL || ""
  ).replace(/\/$/, "");

  if (!base) return url;
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
}

function normalizeCategory(item = {}) {
  return {
    ...item,
    parent_id: item?.parent_id ?? item?.parentId ?? null,
    image_url: item?.image_url ?? item?.imageUrl ?? "",
    imageUrl: item?.imageUrl ?? item?.image_url ?? "",
  };
}

function EntityImage({ src, alt }) {
  if (src) {
    return <img src={getImageUrl(src)} alt={alt || "item"} style={styles.thumb} />;
  }

  return (
    <div style={styles.thumbPlaceholder}>
      {(alt || "?").slice(0, 1).toUpperCase()}
    </div>
  );
}

export default function CatalogManagerDrawer({
  open,
  onClose,
  initialTab = "categories",
  defaultParentCategoryId = "",
  defaultCategoryMode = "main",
  onSaved,
}) {
  const [tab, setTab] = useState(initialTab);

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);

  const [selectedParentId, setSelectedParentId] = useState(
    defaultParentCategoryId || ""
  );

  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [parentCategoryId, setParentCategoryId] = useState(
    defaultParentCategoryId || ""
  );
  const [categoryMode, setCategoryMode] = useState(
    defaultCategoryMode || (defaultParentCategoryId ? "sub" : "main")
  );

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [existingImageUrl, setExistingImageUrl] = useState("");

  const [editingId, setEditingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const isUnitsTab = tab === "units";
  const isBrandsTab = tab === "brands";
  const isCategoriesTab = tab === "categories";
  const isEditing = Boolean(editingId);
  const isImageTab = isCategoriesTab || isBrandsTab;

  const parentCategories = useMemo(() => {
    return [...categories]
      .filter((item) => !item?.parent_id)
      .sort((a, b) => {
        const so = Number(a?.sortOrder ?? a?.sort_order ?? 0) - Number(b?.sortOrder ?? b?.sort_order ?? 0);
        if (so !== 0) return so;
        return String(a?.name || "").localeCompare(String(b?.name || ""));
      });
  }, [categories]);

  const selectedParent = useMemo(() => {
    return parentCategories.find((item) => String(item.id) === String(selectedParentId)) || null;
  }, [parentCategories, selectedParentId]);

  const subcategories = useMemo(() => {
    if (!selectedParentId) return [];
    return [...categories]
      .filter((item) => String(item?.parent_id || "") === String(selectedParentId))
      .sort((a, b) => {
        const so = Number(a?.sortOrder ?? a?.sort_order ?? 0) - Number(b?.sortOrder ?? b?.sort_order ?? 0);
        if (so !== 0) return so;
        return String(a?.name || "").localeCompare(String(b?.name || ""));
      });
  }, [categories, selectedParentId]);

  const compactList = useMemo(() => {
    if (isBrandsTab) return brands;
    if (isUnitsTab) return units;
    return [];
  }, [isBrandsTab, isUnitsTab, brands, units]);

  const resetForm = () => {
    setName("");
    setSymbol("");
    setImageFile(null);
    setImagePreview("");
    setExistingImageUrl("");
    setEditingId("");

    if (isCategoriesTab) {
      setCategoryMode(defaultCategoryMode || (selectedParentId ? "sub" : "main"));
      setParentCategoryId(selectedParentId || defaultParentCategoryId || "");
    } else {
      setCategoryMode("main");
      setParentCategoryId("");
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const [cat, br, un] = await Promise.all([
        CatalogService.getCategories(),
        CatalogService.getBrands(),
        CatalogService.getUnits(),
      ]);

      const normalizedCategories = normalizeList(cat?.data || cat).map(normalizeCategory);
      const normalizedBrands = normalizeList(br?.data || br).map((item) => ({
        ...item,
        image_url: item?.image_url ?? item?.imageUrl ?? "",
        imageUrl: item?.imageUrl ?? item?.image_url ?? "",
      }));

      setCategories(normalizedCategories);
      setBrands(normalizedBrands);
      setUnits(normalizeList(un?.data || un));

const preferredParentId = defaultParentCategoryId || selectedParentId || "";

if (preferredParentId) {
  const existingParent = normalizedCategories.find(
    (item) =>
      !item?.parent_id && String(item.id) === String(preferredParentId)
  );

  setSelectedParentId(existingParent ? existingParent.id : "");
} else {
  setSelectedParentId("");
}
      
    } catch (e) {
      console.error("Catalog load failed:", e);
      setError(
        e?.response?.data?.message || e?.message || "Failed to load catalog data"
      );
      setCategories([]);
      setBrands([]);
      setUnits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setTab(initialTab || "categories");
      loadData();
    }
  }, [open, initialTab]);

  useEffect(() => {
    if (!open) return;

    setError("");
    setSuccess("");
    setEditingId("");

    if (isCategoriesTab) {
      setCategoryMode(defaultCategoryMode || (selectedParentId ? "sub" : "main"));
      setParentCategoryId(selectedParentId || defaultParentCategoryId || "");
      setName("");
      setSymbol("");
      setImageFile(null);
      setImagePreview("");
      setExistingImageUrl("");
    } else {
      setName("");
      setSymbol("");
      setParentCategoryId("");
      setCategoryMode("main");
      setImageFile(null);
      setImagePreview("");
      setExistingImageUrl("");
    }
  }, [tab, open, isCategoriesTab, selectedParentId, defaultParentCategoryId, defaultCategoryMode]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(""), 2500);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;

    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }

    if (!file) {
      setImageFile(null);
      setImagePreview("");
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearSelectedImage = () => {
    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(null);
    setImagePreview("");
  };

const startAddMainCategory = () => {
  setError("");
  setSuccess("");
  setEditingId("");
  setSelectedParentId("");
  setCategoryMode("main");
  setParentCategoryId("");
  setName("");
  setImageFile(null);
  setImagePreview("");
  setExistingImageUrl("");
};

  const startAddSubcategory = () => {
    setError("");
    setSuccess("");
    setEditingId("");
    setCategoryMode("sub");
    setParentCategoryId(selectedParentId || "");
    setName("");
    setImageFile(null);
    setImagePreview("");
    setExistingImageUrl("");
  };

  const handleEdit = (item) => {
    setError("");
    setSuccess("");
    setEditingId(item?.id || "");
    setName(item?.name || "");
    setSymbol(item?.symbol || "");
    const nextParentId = item?.parentId || item?.parent_id || "";
    setParentCategoryId(nextParentId);
    setCategoryMode(nextParentId ? "sub" : "main");
    setImageFile(null);

    if (nextParentId) {
      setSelectedParentId(nextParentId);
    } else if (item?.id) {
      setSelectedParentId(item.id);
    }

    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }

    setImagePreview("");
    setExistingImageUrl(item?.imageUrl || item?.image_url || "");
  };

  const handleDelete = async (id) => {
    const yes = await appConfirm("Are you sure you want to delete this item?", { danger: true, confirmText: "DELETE" });
    if (!yes) return;

    try {
      setError("");
      setSuccess("");

      if (isCategoriesTab) {
        await CatalogService.deleteCategory(id);
      } else if (isBrandsTab) {
        await CatalogService.deleteBrand(id);
      } else {
        await CatalogService.deleteUnit(id);
      }

      if (editingId === id) {
        resetForm();
      }

      setSuccess(
        isCategoriesTab
          ? "Category deleted"
          : isBrandsTab
            ? "Brand deleted"
            : "Unit deleted"
      );

      await loadData();
    } catch (e) {
      console.error("Delete failed:", e);
      setError(
        e?.response?.data?.message || e?.message || "Failed to delete item"
      );
    }
  };

  // const saveCategoryOrder = async (nextItems) => {
  //   await CatalogService.reorderCategories(
  //     nextItems.map((item, index) => ({
  //       id: item.id,
  //       sortOrder: index + 1,
  //     }))
  //   );

  //   await loadData();
  //   await onSaved?.();
  // };

  // const moveCategory = async (item, direction) => {
  //   try {
  //     setError("");
  //     setSuccess("");

  //     const isSub = Boolean(item?.parent_id || item?.parentId);
  //     const list = isSub ? subcategories : parentCategories;

  //     const currentIndex = list.findIndex((x) => String(x.id) === String(item.id));
  //     const nextIndex = currentIndex + direction;

  //     if (currentIndex < 0 || nextIndex < 0 || nextIndex >= list.length) return;

  //     const nextList = [...list];
  //     const [picked] = nextList.splice(currentIndex, 1);
  //     nextList.splice(nextIndex, 0, picked);

  //     await saveCategoryOrder(nextList);

  //     setSuccess("Category order updated");
  //   } catch (e) {
  //     console.error("Reorder failed:", e);
  //     setError(e?.response?.data?.message || e?.message || "Failed to update order");
  //   }
  // };

  const handleSave = async () => {
    const trimmedName = String(name || "").trim();
    const trimmedSymbol = String(symbol || "").trim();

    if (!trimmedName) {
      setError(isUnitsTab ? "Unit name is required" : "Name is required");
      return;
    }

    if (isUnitsTab && !trimmedSymbol) {
      setError("Unit symbol is required");
      return;
    }
if (!isEditing && (isCategoriesTab || isBrandsTab) && !imageFile) {
  setError(
    isBrandsTab
      ? "Brand image is required"
      : categoryMode === "sub"
        ? "Subcategory image is required"
        : "Category image is required"
  );
  return;
}

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      if (isCategoriesTab) {
        const finalParentId =
          categoryMode === "sub"
            ? parentCategoryId || selectedParentId || ""
            : "";

        if (categoryMode === "sub" && !finalParentId) {
          setError("Please select parent category for subcategory");
          setSaving(false);
          return;
        }

        const formData = new FormData();
        formData.append("name", trimmedName);
        formData.append("slug", toSlug(trimmedName));

        if (categoryMode === "sub" && finalParentId) {
          formData.append("parentId", finalParentId);
        }

        if (imageFile) {
          formData.append("image", imageFile);
        }

        if (isEditing) {
          await CatalogService.updateCategory(editingId, formData);
        } else {
          await CatalogService.createCategory(formData);
        }
      } else if (isBrandsTab) {
        const formData = new FormData();
        formData.append("name", trimmedName);
        formData.append("slug", toSlug(trimmedName));

        if (imageFile) {
          formData.append("image", imageFile);
        }

        if (isEditing) {
          await CatalogService.updateBrand(editingId, formData);
        } else {
          await CatalogService.createBrand(formData);
        }
      } else {
        const payload = {
          name: trimmedName,
          symbol: trimmedSymbol,
        };

        if (isEditing) {
          await CatalogService.updateUnit(editingId, payload);
        } else {
          await CatalogService.createUnit(payload);
        }
      }

      setSuccess(
        isEditing
          ? isCategoriesTab
            ? categoryMode === "sub"
              ? "Subcategory updated"
              : "Category updated"
            : isBrandsTab
              ? "Brand updated"
              : "Unit updated"
          : isCategoriesTab
            ? categoryMode === "sub"
              ? "Subcategory added"
              : "Category added"
            : isBrandsTab
              ? "Brand added"
              : "Unit added"
      );

      const keepParent =
        categoryMode === "sub"
          ? parentCategoryId || selectedParentId || ""
          : selectedParentId;

      resetForm();
      await loadData();

      if (keepParent) setSelectedParentId(keepParent);

      await onSaved?.();
    } catch (e) {
      console.error("Save failed:", e);
      setError(
        e?.response?.data?.message || e?.message || "Failed to save item"
      );
    } finally {
      setSaving(false);
    }
  };

  const renderFormCard = () => {
    const title = isCategoriesTab
      ? isEditing
        ? `Update ${categoryMode === "sub" ? "Subcategory" : "Category"}`
        : `Add ${categoryMode === "sub" ? "Subcategory" : "Category"}`
      : isEditing
        ? `Update ${isBrandsTab ? "Brand" : "Unit"}`
        : `Add ${isBrandsTab ? "Brand" : "Unit"}`;

    return (
      <div style={styles.formCard}>
        <div style={styles.formTitleRow}>
          <div style={styles.formTitle}>{title}</div>

          {isEditing ? (
            <button
              type="button"
              onClick={resetForm}
              style={styles.cancelMiniBtn}
            >
              Cancel Edit
            </button>
          ) : null}
        </div>

        <div style={styles.addBox}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              isCategoriesTab
                ? categoryMode === "sub"
                  ? "Enter subcategory name"
                  : "Enter category name"
                : isBrandsTab
                  ? "Enter brand name"
                  : "Enter unit name"
            }
            style={styles.input}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSave();
              }
            }}
          />

          {isCategoriesTab ? (
            <div style={styles.fieldWrap}>
              <div style={styles.fieldLabel}>Type</div>
              <div style={styles.modeRow}>
                <button
                  type="button"
                  onClick={() => {
                    setCategoryMode("main");
                    setParentCategoryId("");
                  }}
                  style={{
                    ...styles.modeBtn,
                    ...(categoryMode === "main" ? styles.modeBtnActive : {}),
                  }}
                >
                  Main Category
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setCategoryMode("sub");
                    setParentCategoryId(selectedParentId || parentCategoryId || "");
                  }}
                  style={{
                    ...styles.modeBtn,
                    ...(categoryMode === "sub" ? styles.modeBtnActive : {}),
                  }}
                >
                  Subcategory
                </button>
              </div>

              {categoryMode === "sub" ? (
                <>
                  <div style={styles.fieldLabel}>Parent Category</div>
                  <select
                    value={parentCategoryId}
                    onChange={(e) => setParentCategoryId(e.target.value)}
                    style={styles.input}
                  >
                    <option value="">Select parent category</option>
                    {parentCategories.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>

                  <div style={styles.helperText}>
                    This subcategory will appear under the selected main category.
                  </div>
                </>
              ) : (
                <div style={styles.helperText}>
                  Main category will appear as a top-level category in the app.
                </div>
              )}
            </div>
          ) : null}

          {isUnitsTab ? (
            <input
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              placeholder="Enter unit symbol"
              style={styles.input}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSave();
                }
              }}
            />
          ) : null}

          {isImageTab ? (
            <div style={styles.imageBox}>
              <label style={styles.fileLabel}>
         <span style={styles.fileLabelText}>
  {isCategoriesTab
    ? categoryMode === "sub"
      ? `Subcategory Image${!isEditing ? " *" : ""}`
      : `Category Image${!isEditing ? " *" : ""}`
    : `Brand Image${!isEditing ? " *" : ""}`}
</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={styles.fileInput}
                />
              </label>

              {imagePreview ? (
                <div style={styles.previewWrap}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={styles.previewImage}
                  />
                  <button
                    type="button"
                    onClick={clearSelectedImage}
                    style={styles.removeImageBtn}
                  >
                    Remove Selected Image
                  </button>
                </div>
              ) : existingImageUrl ? (
                <div style={styles.previewWrap}>
                  <img
                    src={getImageUrl(existingImageUrl)}
                    alt="Current"
                    style={styles.previewImage}
                  />
                  <div style={styles.imageHint}>Current image</div>
                </div>
              ) : (
              <div
  style={{
    ...styles.imageHint,
    color: !isEditing && (isCategoriesTab || isBrandsTab) ? "#dc2626" : styles.imageHint.color,
    fontWeight: !isEditing && (isCategoriesTab || isBrandsTab) ? 700 : styles.imageHint.fontWeight,
  }}
>
  {!isEditing && isBrandsTab
    ? "Brand image is required"
    : !isEditing && isCategoriesTab
      ? categoryMode === "sub"
        ? "Subcategory image is required"
        : "Category image is required"
      : "No image selected"}
</div>
              )}
            </div>
          ) : null}

          <button
            type="button"
            onClick={handleSave}
            disabled={
              saving ||
              !name.trim() ||
              (isUnitsTab && !String(symbol || "").trim())
            }
            style={{
              ...styles.addBtn,
              opacity:
                saving ||
                  !name.trim() ||
                  (isUnitsTab && !String(symbol || "").trim())
                  ? 0.6
                  : 1,
              cursor:
                saving ||
                  !name.trim() ||
                  (isUnitsTab && !String(symbol || "").trim())
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {saving
              ? isEditing
                ? "Updating..."
                : "Adding..."
              : isEditing
                ? "Update"
                : isCategoriesTab
                  ? categoryMode === "sub"
                    ? "Add Subcategory"
                    : "Add Category"
                  : "Add"}
          </button>
        </div>
      </div>
    );
  };

  const renderCategoryManager = () => {
    return (
      <div style={styles.categoryLayout}>
        <div style={styles.parentPanel}>
          <div style={styles.panelHeader}>
            <div>
              <div style={styles.panelTitle}>Main Categories</div>
              <div style={styles.panelSubtext}>
                Select any category to manage its subcategories.
              </div>
            </div>

            <button
              type="button"
              onClick={startAddMainCategory}
              style={styles.smallPrimaryBtn}
            >
              + Main
            </button>
          </div>

          <div style={styles.parentList}>
            {parentCategories.length === 0 ? (
              <div style={styles.empty}>No main categories found</div>
            ) : (
              parentCategories.map((item) => {
                const active = String(selectedParentId) === String(item.id);
                const childCount = categories.filter(
                  (x) => String(x.parent_id || "") === String(item.id)
                ).length;

                return (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => {
                      setSelectedParentId(item.id);
                      if (!isEditing) {
                        setCategoryMode("sub");
                        setParentCategoryId(item.id);
                      }
                    }}
                    style={{
                      ...styles.parentItem,
                      ...(active ? styles.parentItemActive : {}),
                    }}
                  >
                    <div style={styles.parentItemTop}>
                      <EntityImage
                        src={item.imageUrl || item.image_url}
                        alt={item.name}
                      />
                      <div style={styles.parentItemText}>
                        <div style={styles.parentItemName}>{item.name}</div>
                        <div style={styles.parentItemMeta}>
                          {childCount} subcategories
                        </div>
                      </div>
                    </div>

                    <div style={styles.parentItemActions}>
{/* 
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveCategory(item, -1);
                        }}
                        style={styles.editBtn}
                      >
                        ↑
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveCategory(item, 1);
                        }}
                        style={styles.editBtn}
                      >
                        ↓
                      </button> */}

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(item);
                        }}
                        style={styles.editBtn}
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item.id);
                        }}
                        style={styles.deleteBtn}
                      >
                        Delete
                      </button>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div style={styles.subPanel}>
          <div style={styles.panelHeader}>
            <div>
              <div style={styles.panelTitle}>
                {selectedParent ? `${selectedParent.name} Subcategories` : "Subcategories"}
              </div>
              <div style={styles.panelSubtext}>
                Add, edit, delete and manage image of subcategories.
              </div>
            </div>

            <button
              type="button"
              onClick={startAddSubcategory}
              disabled={!selectedParentId}
              style={{
                ...styles.smallPrimaryBtn,
                opacity: selectedParentId ? 1 : 0.5,
                cursor: selectedParentId ? "pointer" : "not-allowed",
              }}
            >
              + Subcategory
            </button>
          </div>

          {renderFormCard()}

          <div style={styles.subList}>
            {!selectedParentId ? (
              <div style={styles.empty}>Select a main category first</div>
            ) : subcategories.length === 0 ? (
              <div style={styles.empty}>No subcategories found for this category</div>
            ) : (
              subcategories.map((item) => (
                <div key={item.id} style={styles.row}>
                  <div style={styles.leftGroup}>
                    <div style={styles.thumbWrap}>
                      <EntityImage
                        src={item.imageUrl || item.image_url}
                        alt={item.name}
                      />
                    </div>

                    <div style={styles.leftContent}>
                      <div style={styles.name}>{item.name || "-"}</div>
                      {item.slug ? (
                        <div style={styles.meta}>Slug: {item.slug}</div>
                      ) : null}
                      <div style={styles.meta}>Type: Subcategory</div>
                    </div>
                  </div>

                  <div style={styles.actionWrap}>

                    {/* <button
                      type="button"
                      onClick={() => moveCategory(item, -1)}
                      style={styles.editBtn}
                    >
                      ↑
                    </button>

                    <button
                      type="button"
                      onClick={() => moveCategory(item, 1)}
                      style={styles.editBtn}
                    >
                      ↓
                    </button> */}
                    <button
                      type="button"
                      onClick={() => handleEdit(item)}
                      style={styles.editBtn}
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                      style={styles.deleteBtn}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCompactManager = () => {
    return (
      <>
        {renderFormCard()}

        <div style={styles.listWrap}>
          {loading ? (
            <div style={styles.empty}>Loading...</div>
          ) : compactList.length === 0 ? (
            <div style={styles.empty}>No {tab} found</div>
          ) : (
            compactList.map((item) => (
              <div key={item.id} style={styles.row}>
                <div style={styles.leftGroup}>
                  {isUnitsTab ? null : (
                    <div style={styles.thumbWrap}>
                      <EntityImage
                        src={item.imageUrl || item.image_url}
                        alt={item.name}
                      />
                    </div>
                  )}

                  <div style={styles.leftContent}>
                    <div style={styles.name}>
                      {item.name || item.symbol || "-"}
                    </div>

                    {isUnitsTab && item.symbol ? (
                      <div style={styles.meta}>Symbol: {item.symbol}</div>
                    ) : null}

                    {!isUnitsTab && item.slug ? (
                      <div style={styles.meta}>Slug: {item.slug}</div>
                    ) : null}
                  </div>
                </div>

                <div style={styles.actionWrap}>
                  <button
                    type="button"
                    onClick={() => handleEdit(item)}
                    style={styles.editBtn}
                  >
                    Edit
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDelete(item.id)}
                    style={styles.deleteBtn}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </>
    );
  };

  if (!open) return null;

  return (
    <div style={styles.overlay} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}>
      <div style={styles.drawer}>
        <div style={styles.header}>
          <div>
            <h3 style={styles.title}>Manage Catalog</h3>
            <div style={styles.subtitle}>Categories, brands and units in one place</div>
          </div>
          <button onClick={onClose} style={styles.closeBtn} type="button" aria-label="Close catalog manager">
            ✕
          </button>
        </div>

        <div style={styles.tabs}>
          <button
            type="button"
            onClick={() => setTab("categories")}
            style={{
              ...styles.tabBtn,
              ...(tab === "categories" ? styles.activeTab : {}),
            }}
          >
            Categories
          </button>

          <button
            type="button"
            onClick={() => setTab("brands")}
            style={{
              ...styles.tabBtn,
              ...(tab === "brands" ? styles.activeTab : {}),
            }}
          >
            Brands
          </button>

          <button
            type="button"
            onClick={() => setTab("units")}
            style={{
              ...styles.tabBtn,
              ...(tab === "units" ? styles.activeTab : {}),
            }}
          >
            Units
          </button>
        </div>

        <div style={styles.content}>
          {error ? <div style={styles.error}>{error}</div> : null}
          {success ? <div style={styles.success}>{success}</div> : null}

          {isCategoriesTab ? (
            <NestedCategoryManager onSaved={onSaved} />
          ) : loading ? (
            <div style={styles.empty}>Loading...</div>
          ) : (
            renderCompactManager()
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.48)",
    backdropFilter: "blur(3px)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
  },
  drawer: {
    width: "min(1240px, 96vw)",
    height: "min(900px, 94vh)",
    background: "#fff",
    borderRadius: 20,
    border: "1px solid rgba(226,232,240,.9)",
    boxShadow: "0 28px 80px rgba(15,23,42,0.22)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "18px 20px 12px",
    flex: "0 0 auto",
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 800,
    color: "#0f172a",
  },
  subtitle: {
    marginTop: 3,
    fontSize: 11,
    fontWeight: 500,
    color: "#94a3b8",
  },
  closeBtn: {
    width: 38,
    height: 38,
    border: "1px solid #e2e8f0",
    borderRadius: 11,
    background: "#fff",
    fontSize: 18,
    cursor: "pointer",
    color: "#475569",
  },
  tabs: {
    display: "flex",
    gap: 8,
    padding: "0 20px 12px",
    borderBottom: "1px solid #eef2f7",
    flexWrap: "wrap",
    flex: "0 0 auto",
  },
  content: {
    minHeight: 0,
    flex: 1,
    overflowY: "auto",
    padding: "16px 20px 20px",
    background: "#fff",
  },
  tabBtn: {
    border: "1px solid #f2c6cc",
    background: "#fff",
    color: "#475569",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  activeTab: {
    background: "#d86a78",
    color: "#fff",
    borderColor: "#d86a78",
  },
  categoryLayout: {
    display: "grid",
    gridTemplateColumns: "320px minmax(0, 1fr)",
    gap: 16,
    alignItems: "start",
  },
  parentPanel: {
    border: "1px solid #f2c6cc",
    borderRadius: 16,
    background: "#fffafb",
    padding: 14,
    position: "sticky",
    top: 0,
  },
  subPanel: {
    border: "1px solid #f2c6cc",
    borderRadius: 16,
    background: "#fff",
    padding: 14,
  },
  panelHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 12,
  },
  panelTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: "#0f172a",
  },
  panelSubtext: {
    marginTop: 4,
    fontSize: 12,
    color: "#64748b",
    lineHeight: 1.4,
  },
  smallPrimaryBtn: {
    border: "none",
    background: "#d86a78",
    color: "#fff",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  parentList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    maxHeight: "calc(100vh - 190px)",
    overflowY: "auto",
    paddingRight: 2,
  },
  parentItem: {
    border: "1px solid #f2c6cc",
    background: "#fff",
    borderRadius: 14,
    padding: 10,
    textAlign: "left",
    cursor: "pointer",
  },
  parentItemActive: {
    border: "1px solid #d86a78",
    boxShadow: "0 0 0 3px #fbe7ea",
  },
  parentItemTop: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  parentItemText: {
    minWidth: 0,
    flex: 1,
  },
  parentItemName: {
    fontSize: 14,
    fontWeight: 700,
    color: "#0f172a",
    wordBreak: "break-word",
  },
  parentItemMeta: {
    marginTop: 4,
    fontSize: 12,
    color: "#64748b",
  },
  parentItemActions: {
    display: "flex",
    gap: 8,
    marginTop: 10,
    justifyContent: "flex-end",
  },
  formCard: {
    border: "1px solid #f8dde1",
    borderRadius: 14,
    background: "#fff8f9",
    padding: 12,
    marginBottom: 14,
  },
  formTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 10,
  },
  formTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#334155",
  },
  cancelMiniBtn: {
    border: "1px solid #f2c6cc",
    background: "#fff",
    color: "#475569",
    borderRadius: 10,
    padding: "7px 10px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  addBox: {
    display: "flex",
    gap: 10,
    marginBottom: 4,
    flexDirection: "column",
  },
  input: {
    width: "100%",
    height: 42,
    borderRadius: 10,
    border: "1px solid #f2c6cc",
    padding: "0 12px",
    outline: "none",
    fontSize: 13,
    background: "#fff",
  },
  fieldWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#475569",
  },
  helperText: {
    fontSize: 11,
    color: "#64748b",
    lineHeight: 1.4,
  },
  modeRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  modeBtn: {
    border: "1px solid #f2c6cc",
    background: "#fff",
    color: "#475569",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  modeBtnActive: {
    background: "#d86a78",
    color: "#fff",
    borderColor: "#d86a78",
  },
  imageBox: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  fileLabel: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  fileLabelText: {
    fontSize: 12,
    fontWeight: 600,
    color: "#475569",
  },
  fileInput: {
    width: "100%",
    fontSize: 12,
    color: "#334155",
  },
  previewWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "flex-start",
  },
  previewImage: {
    width: 84,
    height: 84,
    objectFit: "cover",
    borderRadius: 12,
    border: "1px solid #f2c6cc",
    background: "#fff",
  },
  imageHint: {
    fontSize: 12,
    color: "#64748b",
  },
  removeImageBtn: {
    border: "1px solid #fecdd3",
    background: "#fff1f2",
    color: "#be123c",
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  addBtn: {
    height: 42,
    borderRadius: 10,
    border: "none",
    padding: "0 16px",
    background: "#d86a78",
    color: "#fff",
    fontWeight: 600,
  },
  error: {
    marginBottom: 12,
    padding: 10,
    borderRadius: 10,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#be123c",
    fontSize: 13,
  },
  success: {
    marginBottom: 12,
    padding: 10,
    borderRadius: 10,
    background: "#ecfdf5",
    border: "1px solid #a7f3d0",
    color: "#047857",
    fontSize: 13,
  },
  subList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  listWrap: {
    marginTop: 8,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: 12,
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    background: "#fff",
  },
  leftGroup: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
    flex: 1,
  },
  thumbWrap: {
    flexShrink: 0,
  },
  thumb: {
    width: 48,
    height: 48,
    borderRadius: 10,
    objectFit: "cover",
    border: "1px solid #f2c6cc",
    background: "#fff",
  },
  thumbPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 10,
    border: "1px solid #f2c6cc",
    background: "#fff1f2",
    color: "#d86a78",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    fontWeight: 700,
  },
  leftContent: {
    minWidth: 0,
    flex: 1,
  },
  name: {
    fontSize: 14,
    fontWeight: 600,
    color: "#0f172a",
    wordBreak: "break-word",
  },
  meta: {
    fontSize: 12,
    color: "#64748b",
    marginTop: 4,
    wordBreak: "break-word",
  },
  actionWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  editBtn: {
    border: "1px solid #f2c6cc",
    background: "#fff",
    color: "#d86a78",
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  deleteBtn: {
    border: "1px solid #fecdd3",
    background: "#fff1f2",
    color: "#be123c",
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  empty: {
    padding: 20,
    textAlign: "center",
    color: "#64748b",
    fontSize: 14,
    border: "1px dashed #cbd5e1",
    borderRadius: 12,
    background: "#f8fafc",
  },
};